import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { isWellFormedLobbyCode } from '@srcbattle/shared';
import { api, ApiRequestError, type HomeAppearance, type HomePlacement } from '../api.ts';
import { useSession } from '../session.tsx';
import { Avatar, Banner, Button, Card, Field, Spinner } from '../components/ui.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { HallOfFame } from '../components/HallOfFame.tsx';
import { UnreadDot } from '../components/FeatureRequestThread.tsx';
import { useSiteAssets } from '../siteAssets.ts';
import { GuestInvites, timeLeft } from '../components/GuestInvites.tsx';

interface LobbySummary {
  id: string;
  code: string;
  name: string;
  status: string;
  isHost?: boolean;
}

export function Home({
  onOpenAdmin,
  onOpenStats,
  onOpenProfile,
  onOpenLibrary,
  onOpenRequests,
  onOpenLobby,
}: {
  onOpenAdmin: () => void;
  onOpenStats: () => void;
  onOpenProfile: () => void;
  onOpenLibrary: () => void;
  onOpenRequests: () => void;
  onOpenLobby: (lobbyId: string) => void;
}) {
  const assets = useSiteAssets();
  const { session, signOut, theme, changeTheme } = useSession();

  const [mine, setMine] = useState<LobbySummary[] | null>(null);
  const [open, setOpen] = useState<LobbySummary[]>([]);
  const [joinCode, setJoinCode] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [appearance, setAppearance] = useState<HomeAppearance | null>(null);
  const [homeLayout, setHomeLayout] = useState<'legacy' | 'wide'>(() => localStorage.getItem('srcbattle-home-layout') === 'wide' ? 'wide' : 'legacy');
  const [unreadRequests, setUnreadRequests] = useState(0);
  const isGuest = session?.isGuest ?? false;

  function changeHomeLayout(next: 'legacy' | 'wide') {
    setHomeLayout(next);
    localStorage.setItem('srcbattle-home-layout', next);
    document.body.dataset.pageLayout = next;
  }

  const load = useCallback(async () => {
    try {
      const data = await api.listLobbies();
      setMine(data.mine);
      setOpen(data.open.filter((o) => !data.mine.some((m) => m.id === o.id)));
    } catch {
      setMine([]);
    }
  }, []);

  /**
   * The unread badge, on its own timer.
   *
   * Slower than the lobby list on purpose: a reply to a feature request is not
   * something anybody is waiting on by the second, and this is the only call on
   * the home page that exists purely to render a dot.
   */
  useEffect(() => {
    if (isGuest) return;
    const check = () =>
      void api
        .featureRequestUnread()
        .then(({ unread }) => setUnreadRequests(unread))
        .catch(() => undefined);
    check();
    const timer = window.setInterval(check, 60_000);
    return () => window.clearInterval(timer);
  }, [isGuest]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5_000);
    void api.appearance().then(({ appearance: current }) => setAppearance(current));
    return () => window.clearInterval(timer);
  }, [load]);

  const refreshLobbies = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  async function run(work: () => Promise<{ id: string }>) {
    setBusy(true);
    setError(null);
    try {
      const lobby = await work();
      onOpenLobby(lobby.id);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  function onCreate(event: FormEvent) {
    event.preventDefault();
    void run(() => api.createLobby(newName.trim() || 'Song Battle'));
  }

  function onJoin(event: FormEvent) {
    event.preventDefault();
    if (!isWellFormedLobbyCode(joinCode)) {
      setError('Lobby codes are six characters, like KP3XQ7.');
      return;
    }
    void run(() => api.joinLobby(joinCode));
  }

  if (!session) return null;

  const decorations = (placement: HomePlacement) => appearance && (
    <div className="mb-6 flex flex-col gap-3">
      {appearance.message && appearance.messagePlacement === placement && <p className="whitespace-pre-wrap text-center font-semibold" style={{ fontSize: `${appearance.messageSize}px` }}>{appearance.message}</p>}
      {(['homePhoto1', 'homePhoto2', 'homePhoto3'] as const).map((slot) => assets[slot] && appearance.photos[slot].placement === placement ? (
        <div key={slot} className={`flex ${appearance.photos[slot].align === 'left' ? 'justify-start' : appearance.photos[slot].align === 'right' ? 'justify-end' : 'justify-center'}`}><img src={assets[slot]!} alt="" className="aspect-[3/1] rounded-xl object-fill" style={{ width: `${appearance.photos[slot].size}%` }} /></div>
      ) : null)}
    </div>
  );

  return (
    <main className={`mx-auto w-full px-4 py-8 transition-[max-width] ${homeLayout === 'wide' ? 'max-w-6xl' : 'max-w-2xl'}`}>
      {assets.logo && (
        <img
          src={assets.logo}
          alt=""
          className="mb-6 max-h-20 w-auto"
        />
      )}
      {decorations('top')}
      <header className="mb-8 flex flex-wrap items-center gap-3">
        {/* The whole name block is the way into your own settings: there is no
            room for another button up here, and this is where people look. */}
        <button
          type="button"
          onClick={onOpenProfile}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left transition-colors hover:bg-surface-raised"
        >
          <Avatar value={session.user.avatarValue} size="lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xl font-semibold tracking-tight">
              {session.user.displayName}
            </span>
            <span className="block text-sm text-text-muted">
              {isGuest && session.guestExpiresAt
                ? `Guest · ${timeLeft(session.guestExpiresAt)}`
                : 'Secret Ram Club · edit'}
            </span>
          </span>
        </button>
        <div className="flex flex-wrap gap-2">
          {!isGuest && (
            <Button variant="secondary" onClick={onOpenLibrary}>
              Song library
            </Button>
          )}
          <Button variant="secondary" onClick={onOpenStats}>
            Stats
          </Button>
          {!isGuest && (
            <Button variant="secondary" onClick={onOpenRequests}>
              Requests
              <UnreadDot
                count={unreadRequests}
                label={`${unreadRequests} feature request${unreadRequests === 1 ? '' : 's'} with something new`}
              />
            </Button>
          )}
          {session.isAdmin && (
            <Button variant="secondary" onClick={onOpenAdmin}>
              Members
            </Button>
          )}
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>

      {error && (
        <div className="mb-4">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {decorations('before_lobbies')}

      {/* The two things somebody came here to do, first and together. Both
          lists below are only ever about lobbies that already exist. */}
      <div className={`mb-4 grid gap-4 ${isGuest ? '' : 'sm:grid-cols-2'}`}>
        <Card>
          <form onSubmit={onJoin} className="flex flex-col gap-3">
            <Field
              label="Join a lobby"
              value={joinCode}
              onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
              placeholder="KP3XQ7"
              maxLength={8}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono tracking-widest"
            />
            <Button type="submit" variant="secondary" loading={busy} disabled={!joinCode.trim()}>
              Join
            </Button>
          </form>
        </Card>

        {!isGuest && <Card>
          <form onSubmit={onCreate} className="flex flex-col gap-3">
            <Field
              label="Start a new one"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Friday Night"
              maxLength={60}
            />
            <Button type="submit" loading={busy}>
              Create lobby
            </Button>
          </form>
        </Card>}
      </div>

      {mine === null ? (
        <Spinner label="Loading lobbies" />
      ) : (
        (mine.length > 0 || open.length > 0) && (
          <Card className="mb-4">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-medium">Lobbies</h2>
              {/* The refresh note used to sit alone above everything, which put
                  a housekeeping detail in the first thing anyone read. */}
              <span className="flex items-center gap-2 text-xs text-text-faint">
                <Button variant="ghost" className="px-2 py-1" loading={refreshing} onClick={() => void refreshLobbies()}>
                  Refresh
                </Button>
              </span>
            </div>

            {mine.length > 0 && (
              <ul className="flex flex-col gap-2">
                {mine.map((lobby) => (
                  <li key={lobby.id}>
                    <button
                      type="button"
                      onClick={() => onOpenLobby(lobby.id)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:border-accent"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{lobby.name}</span>
                        <span className="block text-xs text-text-faint">
                          {lobby.isHost ? 'you are hosting' : 'joined'} · {lobby.status}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono text-sm tracking-widest text-text-muted">
                        {lobby.code}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {open.length > 0 && (
              <>
                <h3 className={`text-xs font-medium text-text-muted ${mine.length > 0 ? 'mt-4' : ''}`}>
                  Open right now
                </h3>
                <ul className="mt-2 flex flex-col gap-2">
                  {open.map((lobby) => (
                    <li key={lobby.id} className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-sm">{lobby.name}</span>
                      <Button
                        variant="secondary"
                        onClick={() => void run(() => api.joinLobby(lobby.code))}
                        disabled={busy}
                      >
                        Join
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        )
      )}

      {decorations('after_lobbies')}

      <HallOfFame />

      {!isGuest && <GuestInvites />}

      <details className="mb-4 rounded-xl border border-border bg-surface">
        <summary className="cursor-pointer px-4 py-3 font-medium">Display settings</summary>
        <div className="border-t border-border p-4">
        <h2 className="text-sm font-medium">Page layout</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {([['wide', 'Fill desktop'], ['legacy', 'Centered']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => changeHomeLayout(value)} aria-pressed={homeLayout === value} className={`rounded-lg border px-3 py-2 text-sm ${homeLayout === value ? 'border-accent bg-accent/15' : 'border-border text-text-muted'}`}>{label}</button>)}
        </div>
        <h2 className="mt-5 text-sm font-medium">Theme</h2>
        <ThemePicker theme={theme} onChange={changeTheme} />
        </div>
      </details>

    </main>
  );
}
